import { Text, Box, VStack, Heading, HStack } from '@chakra-ui/react';
import DataTableCard from './DataTableCard';
import { useBinderCardColors } from '../hooks/useBinderCardColors';

interface ConvictionsCardProps {
  data?: any;
}

const ConvictionsCard = ({ data }: ConvictionsCardProps) => {
  const colors = useBinderCardColors();
  const drivers = data?.drivers || [];

  // Common headers for convictions tables
  const convictionHeaders = ['Date', 'Driver', 'Details'];

  // Format empty value display
  const formatEmptyValue = (value: string) => {
    if (!value || value.trim() === '') {
      return (
        <Text color="gray.400" fontStyle="italic" fontSize="sm">
          Not provided
        </Text>
      );
    }
    return value;
  };

  // Create table rows from convictions data
  const createConvictionRows = (convictions: any[]) => {
    if (convictions.length === 0) {
      return [];
    }

    return convictions.map((conviction: any) => [
      formatEmptyValue(conviction.date),
      formatEmptyValue(conviction.driver),
      formatEmptyValue(conviction.details),
    ]);
  };

  // Handle case when no drivers have convictions data
  if (drivers.length === 0) {
    return (
      <Box p={4} borderRadius="md" bg={colors.emptyFieldBgColor}>
        <Text color="gray.500" fontStyle="italic" textAlign="center">
          No driver convictions data available
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {drivers.map((driver: any, index: number) => (
        <Box key={index}>
          {/* Driver Header with Summary */}
          <Box mb={3}>
            <HStack justify="space-between" align="baseline" mb={1}>
              <Heading size="sm" color={colors.textColor}>
                {driver.driverName}
              </Heading>
              <Text fontSize="sm" color={colors.descriptionColor}>
                Total Convictions: {driver.totalConvictions}
              </Text>
            </HStack>
            <HStack spacing={3}>
              <Text fontSize="sm" color={colors.descriptionColor}>
                MVR: {driver.mvrActions?.length || 0} action
                {driver.mvrActions?.length !== 1 ? 's' : ''}
              </Text>
              <Text fontSize="sm" color={colors.descriptionColor}>
                •
              </Text>
              <Text fontSize="sm" color={colors.descriptionColor}>
                Quote: {driver.quoteConvictions?.length || 0} conviction
                {driver.quoteConvictions?.length !== 1 ? 's' : ''}
              </Text>
            </HStack>
          </Box>

          <VStack spacing={3} align="stretch">
            {/* MVR Actions Table */}
            {driver.mvrActions?.length > 0 && (
              <DataTableCard
                description="Convictions, Discharges and Other Actions (MVR)"
                headers={convictionHeaders}
                rows={createConvictionRows(driver.mvrActions)}
              />
            )}

            {/* Quote Convictions Table */}
            {driver.quoteConvictions?.length > 0 && (
              <DataTableCard
                description="Convictions (Quote)"
                headers={convictionHeaders}
                rows={createConvictionRows(driver.quoteConvictions)}
              />
            )}

            {/* Show message if no convictions */}
            {driver.mvrActions?.length === 0 &&
              driver.quoteConvictions?.length === 0 && (
                <Box p={3} bg={colors.emptyFieldBgColor} borderRadius="md">
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">
                    No conviction history found for this driver
                  </Text>
                </Box>
              )}
          </VStack>
        </Box>
      ))}
    </VStack>
  );
};

export default ConvictionsCard;
